import { Routable, RouterPageGroup, RouterPage } from './interfaces';

export function buildRoute(route?: string, prefix?: string) {
  return (prefix ?? '') + (route ?? '');
}

export function isRouterPage(page: Routable): page is RouterPage {
  return (page as RouterPage).content !== undefined;
}

export function isRouterPageGroup(page: Routable): page is RouterPageGroup {
  return (page as RouterPageGroup).pages !== undefined;
}

function isValidRoute(
  route: Routable,
  isAuthenticated?: boolean,
  roles?: string[]
) {
  if (route.authRequired && !isAuthenticated) {
    return false;
  }

  if (route.requiredRoles != null) {
    if (
      (roles == null && route.requiredRoles.length != 0) ||
      !route.requiredRoles.every((r) => roles?.includes(r))
    ) {
      return false;
    }
  }

  return true;
}

export function getValidRoutes(
  routes: Routable[],
  isAuthenticated?: boolean,
  roles?: string[],
  keyPrefix?: string
): Routable[] {
  return routes
    .filter((r) => isValidRoute(r, isAuthenticated, roles))
    .map((r) => {
      const routeKey = r.key || buildRoute(r.route, keyPrefix);

      return isRouterPage(r)
        ? { key: routeKey, ...r }
        : {
            ...r,
            key: routeKey,
            pages: getValidRoutes(r.pages, isAuthenticated, roles, routeKey),
          };
    });
}

export function flattenRoutePages(pages: Routable[]): Routable[] {
  return flatten(pages, (pageGroup) => pageGroup.route == null);
}

export function flattenNamedPages(pages: Routable[]): Routable[] {
  return flatten(
    pages,
    (pageGroup) => pageGroup.menuName == null && pageGroup.icon == null
  );
}

type FlattenWhereClause = (page: RouterPageGroup) => boolean;

function flatten(pages: Routable[], shouldFlatten: FlattenWhereClause) {
  let results: Routable[] = [];

  for (const page of pages) {
    if (isRouterPage(page)) {
      results.push(page);
    } else if (isRouterPageGroup(page)) {
      flattenGroup(results, page, shouldFlatten);
    } else {
      throw Error('Unknown Routable type');
    }
  }

  return results;
}

function flattenGroup(
  results: Routable[],
  pageGroup: RouterPageGroup,
  shouldFlatten: FlattenWhereClause
) {
  const { pages, ...pageGroupInfo } = pageGroup;
  const flattenedPages = flatten(pages, shouldFlatten);

  if (!shouldFlatten(pageGroup)) {
    results.push({ ...pageGroupInfo, pages: flattenedPages });
    return;
  }

  // Not a route, so flatten pages into parent
  for (const page of flattenedPages) {
    const newPage = {
      ...pageGroupInfo,
      ...page,
      route: buildRoute(page.route, pageGroupInfo.route),
    };
    results.push(newPage);
  }
}
